/**
 * 用户认证 Context
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { UserInfo, UserRole } from '../types';
import { authApi, systemLicenseApi } from '../services/api';

interface AuthContextType {
  user: UserInfo | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string }>;
  wechatLogin: (code: string) => Promise<{ success: boolean; message: string }>;
  casdoorLogin: (code: string, ref?: string | null) => Promise<{ success: boolean; message: string }>;
  updateProfile: (data: { display_name?: string; email?: string }) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  /** 授权状态（任何登录用户可查）：status + features（null=不限制），供前端功能菜单控制 */
  license: { status: string; features: string[] | null } | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState<{ status: string; features: string[] | null } | null>(null);

  // 应用启动时尝试恢复登录状态
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      authApi.getMe()
        .then((res) => {
          setUser(res.data);
          setToken(savedToken);
        })
        .catch(() => {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // 在线心跳：登录后立即上报一次，之后每 60s 上报，维护"是否在线"
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      authApi.heartbeat().catch(() => {});
    };
    tick();
    const timer = window.setInterval(tick, 60000);
    return () => window.clearInterval(timer);
  }, [user]);

  // 授权状态：登录后拉取（任何登录用户可查），供前端功能菜单控制
  useEffect(() => {
    if (!user) {
      setLicense(null);
      return;
    }
    let alive = true;
    systemLicenseApi
      .getStatus()
      .then((res) => {
        if (alive) setLicense({ status: res.data.status, features: (res.data.license && res.data.license.features) || null });
      })
      .catch(() => {
        if (alive) setLicense(null);
      });
    return () => {
      alive = false;
    };
  }, [user]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const res = await authApi.login(username, password);
      if (res.data.success && res.data.token) {
        localStorage.setItem('auth_token', res.data.token);
        localStorage.setItem('auth_user', JSON.stringify(res.data.user));
        setToken(res.data.token);
        setUser(res.data.user || null);
        return { success: true, message: res.data.message };
      }
      return { success: false, message: res.data.message || '登录失败' };
    } catch (err: any) {
      return { success: false, message: err?.response?.data?.detail || '登录失败' };
    }
  }, []);

  const wechatLogin = useCallback(async (code: string) => {
    try {
      const res = await authApi.wechatLogin(code);
      if (res.data.success && res.data.token) {
        localStorage.setItem('auth_token', res.data.token);
        localStorage.setItem('auth_user', JSON.stringify(res.data.user));
        setToken(res.data.token);
        setUser(res.data.user || null);
        return { success: true, message: res.data.message };
      }
      return { success: false, message: res.data.message || '微信登录失败' };
    } catch (err: any) {
      return { success: false, message: err?.response?.data?.detail || '微信登录失败' };
    }
  }, []);

  const casdoorLogin = useCallback(async (code: string, ref?: string | null) => {
    try {
      const res = await authApi.casdoorLogin(code, ref);
      if (res.data.success && res.data.token) {
        localStorage.setItem('auth_token', res.data.token);
        localStorage.setItem('auth_user', JSON.stringify(res.data.user));
        setToken(res.data.token);
        setUser(res.data.user || null);
        return { success: true, message: res.data.message };
      }
      return { success: false, message: res.data.message || 'Casdoor 登录失败' };
    } catch (err: any) {
      return { success: false, message: err?.response?.data?.detail || 'Casdoor 登录失败' };
    }
  }, []);

  const updateProfile = useCallback(async (data: { display_name?: string; email?: string }) => {
    try {
      const res = await authApi.updateProfile(data);
      const updatedUser = res.data;
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true, message: '个人信息已更新' };
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      return { success: false, message: typeof detail === 'string' ? detail : '更新失败' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 忽略后端错误，本地清除即可
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, wechatLogin, casdoorLogin, updateProfile, logout, isAdmin, license }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
